import type {
  ApolloClient,
  ApolloQueryResult,
  InMemoryCache,
} from '@apollo/client';

import audioIcon from '@app/assets/images/audioIcon.svg';
import pauseIconSvg from '@app/assets/images/pauseIcon.svg';
import type { AudioPlayerActions } from '@app/redux/actions/audioPlayer';

import type { getMedia } from '../../pages/ProductFinder/__generated__/getMedia';
import GET_MEDIA_ITEM_QUERY from '../../pages/ProductFinder/getMedia.query';
import AE from '../AudioPlayer/AudioEngine';
import MediaIcon from '../MediaIcon';

export interface AudioPlayerTriggerProps {
  audioId: number | string;
  audioPlayer;
  dispatchers: AudioPlayerActions;
  client: ApolloClient<InMemoryCache>;
}

const AudioPlayerTrigger = ({
  audioId,
  audioPlayer,
  client,
  dispatchers,
}: AudioPlayerTriggerProps) => {
  const {
    playAudioPlayer,
    pauseAudioPlayer,
    resetAudioPlayer,
    setId,
    loadAudio,
    setData,
  } = dispatchers;

  const isAudioPlayerTarget = audioPlayer.id === audioId;
  const isAudioPlaying = isAudioPlayerTarget && audioPlayer.isPlaying;
  const isAudioLoading = isAudioPlayerTarget && audioPlayer.isAudioLoading;

  const getMedia = async (
    id: string | number,
  ): Promise<ApolloQueryResult<getMedia>> =>
    client.query({
      query: GET_MEDIA_ITEM_QUERY,
      variables: { id: Number(id) },
    });

  const onClick = async (): Promise<undefined | null> => {
    try {
      if (isAudioPlaying) {
        AE.pause();
        pauseAudioPlayer();
      } else if (isAudioPlayerTarget) {
        AE.play();
        playAudioPlayer();
      } else {
        AE.reset();
        resetAudioPlayer();
        setId(audioId);
        loadAudio();
        const queryResult = await getMedia(audioId);
        setData(queryResult.data.media);
      }
      return null;
    } catch {
      return null;
    }
  };

  return (
    <MediaIcon
      isLoading={isAudioLoading}
      onClick={onClick}
      svg={isAudioPlaying ? pauseIconSvg : audioIcon}
      altText="play audio"
    />
  );
};

export default AudioPlayerTrigger;
